import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserSidebarLayout } from '../components/UserSidebarLayout';
import { COLORS } from '../../../core/constants/colors';
import { Session } from '../types';
import { api } from '../../../core/utils/apiClient';

export const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setIsLoading(true);

      const response = await api.get('/api/v1/llm/sessions?limit=100');

      if (response.ok) {
        const data = await response.json();
        setSessions(data || []);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSessions = sessions.filter((session) => {
    const matchesSearch = (session.title || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (isLoading) {
    return (
      <UserSidebarLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: COLORS.primary.persianIndigo }}>Loading history...</p>
          </div>
        </div>
      </UserSidebarLayout>
    );
  }

  return (
    <UserSidebarLayout>
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#1A1A1A' }}>
            Query History
          </h1>
          <p className="text-gray-600">
            View all your AI chat sessions
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <svg className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-violet-500"
              style={{ borderColor: '#E0E0E0' }}
            />
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - Query List */}
          <div className="lg:col-span-2 space-y-4">
            {filteredSessions.length > 0 ? (
              filteredSessions.map((session) => (
                <div
                  key={session.session_id}
                  onClick={() => setSelectedSession(session)}
                  className={`rounded-xl p-6 shadow-sm cursor-pointer transition-all hover:shadow-md ${selectedSession?.session_id === session.session_id ? 'ring-2 ring-violet-500' : ''}`}
                  style={{ backgroundColor: '#FAFAFA' }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                      style={{ backgroundColor: COLORS.user.queryButton }}
                    >
                      S
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-base mb-2" style={{ color: '#1A1A1A' }}>
                        {session.title || 'Untitled Session'}
                      </h3>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs text-gray-500">
                          {new Date(session.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/chat/${session.session_id}`);
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: COLORS.user.primary }}
                    >
                      Open Chat
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-xl p-12 text-center" style={{ backgroundColor: '#FAFAFA' }}>
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <h3 className="text-lg font-bold mb-2" style={{ color: '#1A1A1A' }}>
                  No sessions found
                </h3>
                <p className="text-gray-600 mb-4">
                  Start a new chat to see it here
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-3 rounded-xl font-semibold text-white transition-all hover:shadow-lg"
                  style={{ backgroundColor: COLORS.extended.orange }}
                >
                  Start New Chat
                </button>
              </div>
            )}
          </div>

          {/* Right - Selected Query Detail */}
          <div className="lg:col-span-1">
            {selectedSession ? (
              <div className="rounded-xl p-6 shadow-sm sticky top-8" style={{ backgroundColor: '#FAFAFA' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: COLORS.extended.azure + '20' }}
                  >
                    <svg className="w-5 h-5" style={{ color: COLORS.extended.azure }} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg" style={{ color: '#1A1A1A' }}>
                    Session Details
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Title</p>
                    <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>
                      {selectedSession.title || 'Untitled Session'}
                    </p>
                  </div>

                  <div className="pt-4 border-t space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Session ID</span>
                      <span className="text-xs font-medium" style={{ color: '#1A1A1A' }}>
                        {selectedSession.session_id}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Created At</span>
                      <span className="text-xs font-medium" style={{ color: '#1A1A1A' }}>
                        {new Date(selectedSession.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Last Updated</span>
                      <span className="text-xs font-medium" style={{ color: '#1A1A1A' }}>
                        {new Date(selectedSession.updated_at).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`/dashboard/chat/${selectedSession.session_id}`)}
                    className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:shadow-lg mt-4"
                    style={{ backgroundColor: COLORS.user.primary }}
                  >
                    Continue Chat
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-xl p-6 shadow-sm sticky top-8" style={{ backgroundColor: '#FAFAFA' }}>
                <div className="text-center py-8">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-600">
                    Select a session to view details
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </UserSidebarLayout>
  );
};

